import { addDays, addMinutes, format } from 'date-fns'
import { ja } from 'date-fns/locale'
import React, { FC, useEffect } from 'react'
import { Fragment } from 'react'
import styled from 'styled-components'
import { mutate } from 'swr'
import { getPrevDateByDay } from '../../../common/utils/date'
import { useCalendarContext } from '../../../context/calendar'
import usePendingSchedules from '../../../hooks/usePendingSchedules'
import useWindowWidth from '../../../hooks/useWindowSize'
import { setSchedule } from '../../../options/setSchedule'
import { useAuth } from 'hooks/use-auth'

const DropZoneTimeTable: FC = () => {
  const auth = useAuth();
  const { date, filtered, mode } = useCalendarContext()
  const { pendingSchedules } = usePendingSchedules()
  const windowWidth = useWindowWidth()
  const [isFirstRender, setIsFirstRender] = React.useState(true)
  const sunday = getPrevDateByDay(date, 'sunday')
  const hoursList = [
    0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24,
  ]
  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.currentTarget.style.background = '#0ea4e966'
  }
  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.currentTarget.style.background = 'initial'
  }
  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    const startDateTime = new Date(
      `${event.currentTarget.dataset.date} ${event.currentTarget.dataset.hours}:${event.currentTarget.dataset.minutes}`
    )
    const columnId = event.currentTarget.dataset.columnId
    const scheduleId = event.dataTransfer.getData('Text')
    const targetSchedule = pendingSchedules?.filter(
      (pendingSchedule) => pendingSchedule.id.toString() === scheduleId
    )[0]

    if (targetSchedule && columnId) {
      const endDatetime = addMinutes(startDateTime, targetSchedule.estimated_time)
      setSchedule(targetSchedule.id, startDateTime, endDatetime, columnId, auth.factoryId, auth.authorizationHeader).then(() => {
        mutate(['/api/schedules', date, filtered])
        mutate('/api/pendingSchedules')
      })
    }
    event.currentTarget.style.background = 'initial'
  }

  useEffect(() => {;
    // 初回は0.5秒待ってスクロール
    if (isFirstRender) {
      setIsFirstRender(false)
      setTimeout(() => {
        document.getElementById(format(date, 'yyyy-MM-dd'))?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
      }, 500)
      return
    }

    document.getElementById(format(date, 'yyyy-MM-dd'))?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    })
  }, [date])

  return (
    <>
      {[...Array(7)].map((_, dateIndex) => {
        const workDate = new Date(new Date(sunday).setDate(sunday.getDate() + dateIndex))

        return (
          <DateRow style={{ height: 480 }} key={dateIndex}>
            <SideCell  style={{ color: '#71717a' }} id={format(workDate, 'yyyy-MM-dd')}>
              {mode === 'date' ? (
                <div style={{ marginBottom: -16, position: 'relative' }}>
                  <DateBorder style={{ width: '82.5px' }}></DateBorder>
                  <DateBorderText>{format(workDate, 'MM/dd')}</DateBorderText>
                </div>
              ) : (
                <Day>
                  {format(workDate, 'dd')}&nbsp;{format(workDate, 'eee', { locale: ja })}
                </Day>
              )}
              {mode === 'date' &&
                hoursList.map((hours) => {
                  if (hours === 24) {
                    return <Fragment key={hours}></Fragment>
                  } else {
                    return <SideHourCell key={hours}>{hours}:00</SideHourCell>
                  }
                })}
            </SideCell>
            {filtered.map((filter, filterIndex) => {
              return (
                <DateCell
                  key={filter.id}
                  isSelectedDate={date.getDate() === workDate.getDate()}
                  style={{ borderBottom: 'none' }}
                >
                  <DateBorder
                    style={{
                      minWidth: 145,
                      width: `${(windowWidth - 300 - 82.5) / filtered.length}px`,
                    }}
                  />
                  {hoursList.map((hours) => {
                    if (hours === 24) {
                      if (dateIndex === 6) {
                        return (
                          <HoursCell
                            isLast={filtered.length === filterIndex + 1}
                            key={hours}
                            minHeight={0}
                            data-hours={0} // 24ではなく0
                            data-date={format(addDays(workDate, 1), 'yyyy-MM-dd')}
                            data-minutes={0}
                            data-column-id={filter.id}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                          />
                        )
                      } else {
                        return <Fragment key={hours} />
                      }
                    }
                    return (
                      <Fragment key={hours}>
                        <HoursCell
                          isLast={filtered.length === filterIndex + 1}
                          minHeight={5}
                          data-hours={hours}
                          data-date={format(workDate, 'yyyy-MM-dd')}
                          data-minutes={0}
                          data-column-id={filter.id}
                          onDragOver={handleDragOver}
                          onDragLeave={handleDragLeave}
                          onDrop={handleDrop}
                        />
                        <HoursCell
                          isLast={filtered.length === filterIndex + 1}
                          minHeight={5}
                          data-hours={hours}
                          data-date={format(workDate, 'yyyy-MM-dd')}
                          data-minutes={15}
                          data-column-id={filter.id}
                          onDragOver={handleDragOver}
                          onDragLeave={handleDragLeave}
                          onDrop={handleDrop}
                        />
                        <HoursCell
                          isLast={filtered.length === filterIndex + 1}
                          minHeight={5}
                          data-hours={hours}
                          data-date={format(workDate, 'yyyy-MM-dd')}
                          data-minutes={30}
                          data-column-id={filter.id}
                          onDragOver={handleDragOver}
                          onDragLeave={handleDragLeave}
                          onDrop={handleDrop}
                        />
                        <HoursCell
                          isLast={filtered.length === filterIndex + 1}
                          minHeight={5}
                          showBorder={mode === 'date'}
                          data-hours={hours}
                          data-date={format(workDate, 'yyyy-MM-dd')}
                          data-minutes={45}
                          data-column-id={filter.id}
                          onDragOver={handleDragOver}
                          onDragLeave={handleDragLeave}
                          onDrop={handleDrop}
                        />
                      </Fragment>
                    )
                  })}
                </DateCell>
              )
            })}
          </DateRow>
        )
      })}
    </>
  )
}

export default DropZoneTimeTable

const SideCell = styled.div`
  width: 80.5px;
  margin-left: -80.5px;
  flex-shrink: 0;
  position: sticky;
  left: 0;
  /* background-color: #FFF; */
  /* flex-grow: 1; */
`

const SideHourCell = styled.div`
  height: 20px;
  margin-left: 34px;
  font-size: 14px;
`

const DateBorder = styled.div`
  border-bottom: solid 2px #71717a;
`

const DateBorderText = styled.div`
  font-family: Inter;
  font-size: 10px;
  font-weight: 800;
  line-height: 16px;
  letter-spacing: 0em;
`

type DayProps = {
  isSelectedDate?: boolean
}

const Day = styled.div<DayProps>`
  flex: 1;
  font-family: Inter;
  font-size: 20px;
  font-weight: 500;
  line-height: 30px;
  letter-spacing: 0em;
  text-align: left;
  padding: 4px 8px 16px;
  background-color: ${({ isSelectedDate }) => (isSelectedDate ? '#62C234' : 'initial')};
  > div {
    /* 選択されている日であれば子要素の文字色を強制的に変更 */
    color: ${({ isSelectedDate }) => (isSelectedDate ? '#FFF!important' : 'initial')};
  }
`

const DateRow = styled.div`
  height: 144px;
  display: flex;
  justify-content: space-between;
  word-break: break-all;
`

// const Hour = styled.div`
//   font-family: Inter;
//   font-size: 10px;
//   font-weight: 500;
//   line-height: 16px;
//   letter-spacing: 0em;
//   text-align: left;
// `

type DateCellProps = {
  isSelectedDate: boolean
}

const DateCell = styled.div<DateCellProps>`
  min-width: 145px;
  flex: 1;
  border-bottom: solid 1px #8989894a;
  background-color: ${({ isSelectedDate }) => (isSelectedDate ? '#8C8C8C1A' : 'transparent')};
`

type HoursCellProps = {
  isLast: boolean
  minHeight?: number
  showBorder?: boolean
}

const HoursCell = styled.div<HoursCellProps>`
  min-height: ${({ minHeight }) => minHeight}px;
  border-bottom: ${({ showBorder }) => (showBorder ? 'solid 1px #A6A6A61A' : 'none')};
  border-right: ${({ isLast }) => (isLast ? 'none' : 'solid 1px #E0E0E0')};
`
